<app-menu-common url="hr"></app-menu-common>

@if (hrService.backendMissing()) {
	<div class="hr-backend-warn">Backend RH non déployé : données indisponibles (voir docs/backend-brief-hr.md).</div>
}

<!-- Session périmée : les lectures personnelles (absences, pointages) reviennent VIDES. Sans ce
     bandeau, le planning s'affiche complet mais sans congés — et rien ne dit pourquoi. -->
@if (hrService.sessionExpired()) {
	<div class="hr-backend-warn">Session expirée ou incomplète : absences et pointages ne sont pas affichés. Reconnectez-vous.</div>
}

<div class="flexrow hr-header">
	<button mat-icon-button aria-label="Semaine précédente" (click)="onPrevWeek()">
		<mat-icon>chevron_left</mat-icon>
	</button>
	<span class="week-label">{{ weekLabel }}</span>
	<button mat-icon-button aria-label="Semaine suivante" (click)="onNextWeek()">
		<mat-icon>chevron_right</mat-icon>
	</button>
	<button mat-stroked-button (click)="onToday()">Aujourd'hui</button>

	<span class="spacer"></span>

	<!-- Jours de fermeture du magasin : masqués par défaut, réaffichés par le toggle. -->
	@if (hiddenDaysCount > 0 || showClosedDays) {
		<mat-slide-toggle class="closed-toggle" [checked]="showClosedDays" (change)="onToggleClosedDays($event.checked)"
			matTooltip="Afficher aussi les jours de fermeture du magasin">Jours fermés</mat-slide-toggle>
	}

	<!-- P04 — « où travaille chaque salarié ? » ⇄ « qui tient chaque poste ? » (mêmes données). -->
	<mat-button-toggle-group class="view-toggle" [value]="viewMode" (change)="onViewMode($event.value)">
		<mat-button-toggle value="employees" matTooltip="Une ligne par salarié — où travaille-t-il à chaque instant">Salariés</mat-button-toggle>
		<mat-button-toggle value="postes" matTooltip="Une ligne par service — qui est là, et combien">Postes</mat-button-toggle>
	</mat-button-toggle-group>

	<!-- « Tous » en tête ET par défaut : le planning s'ouvre sur toute l'organisation, chaque
	     établissement se choisit ensuite pour s'y concentrer. -->
	<mat-form-field class="store-select">
		<mat-label>Magasin</mat-label>
		<mat-select [formControl]="storeCtrl">
			<mat-option [value]="null">Tous</mat-option>
			@for (s of stores; track s.id) {
				<mat-option [value]="s.id">{{ s.name }}</mat-option>
			}
		</mat-select>
	</mat-form-field>

	<!-- O05 — filtre d'équipe : restreint l'affichage sans toucher à l'organisation. -->
	@if (teams.length > 0) {
		<mat-form-field class="store-select">
			<mat-label>Équipe</mat-label>
			<mat-select [formControl]="teamCtrl">
				<mat-option [value]="null">Toutes</mat-option>
				@for (t of teams; track t.id) {
					<mat-option [value]="t.id">{{ t.name }}</mat-option>
				}
			</mat-select>
		</mat-form-field>
	}

	<!-- Impression : MÊME PLACE que dans l'éditeur d'horaires et « Tous les horaires » — dernier
	     bouton-icône du groupe de droite, juste avant l'action principale de l'écran. -->
	<button mat-icon-button aria-label="Imprimer" matTooltip="Imprimer : planning, feuille d'heures ou horaires types"
		(click)="onPrint()">
		<mat-icon>print</mat-icon>
	</button>

	<!-- L6 — publication : seuls les salariés dont le planning a changé sont notifiés.
	     Geste de responsable RH : le salarié consulte son planning, il ne le publie pas. -->
	@if (isHrManager) {
		<button mat-stroked-button matTooltip="Publier le planning et notifier les salariés concernés" (click)="onPublish()">
			<mat-icon>send</mat-icon>&nbsp;Publier
		</button>
	}
</div>

<div class="pagecontent">
	@if (loading) {
		<div class="hr-loading">Chargement de la semaine…</div>
	}

	<!-- ============================================================
	     VUE SALARIÉ — une ligne par personne, tous services/magasins
	     sur la même ligne. La couleur dit le SERVICE, la barre le lieu.
	     ============================================================ -->
	@if (viewMode === 'employees' && !loading) {
		<div class="section">
			<div class="grid-scroll">
				<table class="week-grid emp-grid">
					<thead>
						<tr>
							<th class="name-col">Salarié</th>
							@for (dayIdx of visibleDayIndices; track dayIdx) {
								<th [class.holiday]="weekDays[dayIdx].holiday" [class.today]="weekDays[dayIdx].today"
									[class.locked]="weekDays[dayIdx].locked" [class.closed]="!weekDays[dayIdx].open">
									<div class="day-title">
										{{ weekDays[dayIdx].label }}
										<span class="day-flags">
											@if (weekDays[dayIdx].locked) {
												<mat-icon class="lock" [matTooltip]="lockTooltip(weekDays[dayIdx])">lock</mat-icon>
											}
											<!-- Le salarié LIT la note du jour ; il n'en pose pas (icône vide masquée). -->
											@if (isHrManager || weekDays[dayIdx].note) {
												<mat-icon class="note" [class.filled]="weekDays[dayIdx].note"
													[matTooltip]="weekDays[dayIdx].note || 'Ajouter une note pour cette journée'"
													(click)="onEditNote(weekDays[dayIdx])">sticky_note_2</mat-icon>
											}
											<!-- Fin de journée : figer les heures réellement réalisées (jours écoulés seulement). -->
											@if (canCloseDay(weekDays[dayIdx])) {
												<mat-icon class="close-day"
													matTooltip="Clôturer la journée : ajuster les heures réellement réalisées"
													(click)="onCloseDay(weekDays[dayIdx])">task_alt</mat-icon>
											}
										</span>
									</div>
									@if (weekDays[dayIdx].holiday) {
										<div class="holiday-name">{{ weekDays[dayIdx].holiday }}</div>
									}
									<div class="day-axis">
										@for (m of weekDays[dayIdx].markers; track $index) {
											<span class="axis-label" [class.close]="m.kind === 'close'" [style.left.%]="m.left_pct">{{ m.label }}</span>
										}
										<div class="axis-line">
											@for (b of weekDays[dayIdx].bands; track $index) {
												<span class="axis-band" [style.left.%]="b.left_pct" [style.width.%]="b.width_pct"></span>
											}
											@for (m of weekDays[dayIdx].markers; track $index) {
												<span class="axis-mark" [class.close]="m.kind === 'close'" [style.left.%]="m.left_pct"
													[matTooltip]="(m.kind === 'open' ? 'Ouverture ' : 'Fermeture ') + m.label"></span>
											}
										</div>
									</div>
								</th>
							}
							<th class="total-col">Semaine</th>
						</tr>
					</thead>
					<!-- L'ORDRE DES LIGNES se range à la main et s'enregistre (c_values 30) : l'alphabet ne
					     dit rien d'une équipe. Même geste que dans « Horaires types » et que les livraisons
					     d'une tournée — on prend la ligne par sa poignée, elle monte ou descend. -->
					<tbody cdkDropList [cdkDropListData]="employeeRows" cdkDropListLockAxis="y"
						(cdkDropListDropped)="onRowDrop($event)">
						@for (row of employeeRows; track row.id_employee) {
							<!-- Ce qui suit la souris est la LIGNE ELLE-MÊME, à la taille de la grille : la
							     personne emmène ses heures, et c'est bien ce qu'on voit se déplacer. -->
							<tr [class.row-moving]="movingId === row.id_employee"
								cdkDrag cdkDragLockAxis="y" [cdkDragData]="row" [cdkDragDisabled]="!isHrManager"
								cdkDragPreviewClass="row-band-wrap"
								(cdkDragStarted)="onRowLift(row)" (cdkDragEnded)="onRowDropped()">

								<!-- La racine ne sert que d'ancre : le CDK la pose sur le point de saisie et lui
								     impose sa position. La bande, elle, se replace par rapport à cette ancre —
								     c'est ainsi qu'elle démarre exactement sur la ligne d'origine. -->
								<ng-template cdkDragPreview>
									<div class="row-preview">
										<div class="row-band" [style.left.px]="rowBand.left" [style.top.px]="rowBand.top"
											[style.width.px]="rowBand.width" [style.height.px]="rowBand.height">
											<div class="band-cells" [style.left.px]="rowBand.innerLeft">
												@for (cell of bandCells; track $index) {
													<div class="band-cell" [style.left.px]="cell.left" [style.width.px]="cell.width">
														@switch (cell.kind) {
															@case ('name') { <span class="band-name">{{ row.name }}</span> }
															@case ('total') { <span class="band-total">{{ totalLabel(row) }}</span> }
															@default {
																<!-- La piste reprend la géométrie de la case : les barres tombent aux mêmes
																     pixels que dans la grille, sans quoi la bande ne se superposerait pas
																     à la ligne qu'elle est censée soulever. -->
																<div class="band-track">
																	@if (fullDayAbsence(row.cells[cell.dayIdx]); as badge) {
																		<span class="band-absence" [style.--absence-color]="badge.color">{{ badge.label }}</span>
																	} @else {
																		@for (s of row.cells[cell.dayIdx].slots; track $index) {
																			<span class="band-bar" [class.absent]="s.absent"
																				[style.left.%]="barOf(s).left_pct" [style.width.%]="barOf(s).width_pct"
																				[style.background]="s.absent ? null : slotColor(s)"
																				[style.color]="s.absent ? null : (slotColor(s) | hrTextOn)">{{ placeShort(s) }}</span>
																		}
																	}
																</div>
															}
														}
													</div>
												}
											</div>
										</div>
									</div>
								</ng-template>

								<td class="name-col">
									<!-- Poignée de rangement : à gauche, là où l'œil cherche une prise. Elle n'existe
									     que pour le responsable RH — l'ordre est un document d'organisation. -->
									@if (isHrManager) {
										<span class="row-grip" cdkDragHandle (pointerdown)="onRowGrab($event)"
											matTooltip="Glisser pour ranger ce salarié dans la liste (ordre enregistré)"
											[attr.aria-label]="'Déplacer la ligne de ' + row.name">
											<mat-icon>drag_indicator</mat-icon>
										</span>
									}
									<!-- Le nom mène à SA fiche. Vraie balise <a> : ctrl/shift+clic = nouvel onglet,
									     et la semaine affichée reste sous les yeux. -->
									<a class="emp-link" [routerLink]="['/hr/employee', row.id_employee]"
										matTooltip="Ouvrir la fiche employé">{{ row.name }}</a>
								</td>
								@for (dayIdx of visibleDayIndices; track dayIdx) {
									<td class="slot-cell" [class.holiday]="weekDays[dayIdx].holiday" [class.today]="weekDays[dayIdx].today"
										(click)="onEmployeeCellClick(row, dayIdx)">
										<div class="slot-track" #trk>
											@for (b of weekDays[dayIdx].bands; track $index) {
												<span class="track-band" [style.left.%]="b.left_pct" [style.width.%]="b.width_pct"></span>
											}
											<!-- Congé / absence : UN bloc gris sur toute la journée. L'horaire qu'il
											     remplace passe en infobulle plutôt que de rester en barres barrées. -->
											@if (fullDayAbsence(row.cells[dayIdx]); as badge) {
												<span class="absence-block" [class.pending]="badge.pending"
													[style.--absence-color]="badge.color"
													[matTooltip]="absenceTooltip(row.cells[dayIdx])">{{ badge.label }}</span>
											} @else {
												@for (s of row.cells[dayIdx].slots; track s) {
													<!-- La barre : longueur = temps de travail, couleur = service. Deux lignes de
													     libellé : le LIEU puis le POSTE. On la glisse pour déplacer, on tire un bord. -->
													<span class="slot-bar" [class.absent]="s.absent" [class.pending-absence]="s.absence_pending"
														[style.left.%]="barOf(s).left_pct"
														[style.width.%]="barOf(s).width_pct"
														[style.background]="s.absent ? null : slotColor(s)"
														[style.color]="s.absent ? null : (slotColor(s) | hrTextOn)"
														[matTooltip]="placeLabel(s) + ' · ' + slotLabel(s) + (s.break_min ? ' — ' + s.break_min + ' mn de pause' : '')"
														(click)="$event.stopPropagation()"
														(pointerdown)="onBarPointerDown($event, s, trk, 'move')">
														@if (!s.absent) {
															<span class="handle left" (pointerdown)="onBarPointerDown($event, s, trk, 'start')"></span>
														}
														<span class="bar-text">
															<span class="bar-line">{{ placeShort(s) }}</span>
															@if (activityShort(s); as poste) {
																<span class="bar-line poste">{{ poste }}</span>
															}
														</span>
														@if (!s.absent) {
															<span class="handle right" (pointerdown)="onBarPointerDown($event, s, trk, 'end')"></span>
														}
													</span>
												}
											}
										</div>
										@if (row.cells[dayIdx].absence; as badge) {
											@if (!fullDayAbsence(row.cells[dayIdx])) {
												<!-- Demande en attente : le travail reste prévu, la pastille se range sous la piste. -->
												<span class="absence-chip" [class.pending]="badge.pending" [style.--absence-color]="badge.color">{{ badge.label }}</span>
											}
										}
										@if (cellIssues(row, dayIdx).length > 0) {
											<mat-icon class="issue" [matTooltip]="issuesTooltip(cellIssues(row, dayIdx))">warning</mat-icon>
										}
										@if (availabilityBadge(row, dayIdx); as av) {
											<span class="avail-flag" [class.unavailable]="av.kind === 2" [matTooltip]="av.tooltip"></span>
										}
										@if (availabilityConflicts(row, dayIdx); as conflict) {
											<mat-icon class="avail-conflict" [matTooltip]="conflict">report_problem</mat-icon>
										}
									</td>
								}
								<td class="total-col" [class.no-count]="row.counts_hours === false">
									<span [matTooltip]="totalTooltip(row)">{{ totalLabel(row) }}</span>
									@if (deltaLabel(row)) {
										<span class="delta" [class]="deltaClass(row)" [matTooltip]="deltaTooltip(row)">{{ deltaLabel(row) }}</span>
									}
									<!-- Le compte des JOURS à côté des heures : « 3 j CP » dit tout de suite
									     pourquoi la semaine tombe juste, et distingue le justifié de l'injustifié. -->
									@for (chip of absenceChips(row); track chip.code) {
										<span class="abs-chip" [class]="chip.code" [matTooltip]="chip.tooltip">{{ chip.label }}</span>
									}
									@if (weekIssues(row).length > 0) {
										<mat-icon class="issue" [matTooltip]="issuesTooltip(weekIssues(row))">warning</mat-icon>
									}
								</td>
							</tr>
						}
						<!-- Ajout naturel : ligne toujours présente qui commence par un select.
						     Composer l'équipe est un geste de responsable RH. -->
						@if (isHrManager) {
						<tr class="add-emp-row">
							<td class="name-col">
								<mat-form-field class="add-emp-select" subscriptSizing="dynamic">
									<mat-label>＋ salarié</mat-label>
									<mat-select [formControl]="addEmpCtrl">
										@for (e of addableEmployees; track e.id) {
											<mat-option [value]="e.id">{{ e.name }}</mat-option>
										}
										<!-- Une liste vide qui s'ouvre sur rien passe pour une panne : on le dit. -->
										@if (addableEmployees.length === 0) {
											<mat-option [disabled]="true">Tout le monde est déjà affiché</mat-option>
										}
									</mat-select>
								</mat-form-field>
							</td>
							@for (dayIdx of visibleDayIndices; track dayIdx) {
								<td class="slot-cell empty"></td>
							}
							<td class="total-col"></td>
						</tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	}

	<!-- ============================================================
	     VUE POSTE — une ligne par service, les personnes en barres.
	     Répond à « combien de monde, et qui, sur ce poste ? ».
	     ============================================================ -->
	@if (viewMode === 'postes' && !loading) {
		<div class="section">
			<div class="grid-scroll">
				<table class="week-grid poste-grid">
					<thead>
						<tr>
							<th class="name-col">Poste</th>
							@for (dayIdx of visibleDayIndices; track dayIdx) {
								<th [class.holiday]="weekDays[dayIdx].holiday" [class.today]="weekDays[dayIdx].today"
									[class.locked]="weekDays[dayIdx].locked" [class.closed]="!weekDays[dayIdx].open">
									<div class="day-title">
										{{ weekDays[dayIdx].label }}
										<span class="day-flags">
											@if (weekDays[dayIdx].locked) {
												<mat-icon class="lock" [matTooltip]="lockTooltip(weekDays[dayIdx])">lock</mat-icon>
											}
											<!-- Le salarié LIT la note du jour ; il n'en pose pas (icône vide masquée). -->
											@if (isHrManager || weekDays[dayIdx].note) {
												<mat-icon class="note" [class.filled]="weekDays[dayIdx].note"
													[matTooltip]="weekDays[dayIdx].note || 'Ajouter une note pour cette journée'"
													(click)="onEditNote(weekDays[dayIdx])">sticky_note_2</mat-icon>
											}
											<!-- Fin de journée : figer les heures réellement réalisées (jours écoulés seulement). -->
											@if (canCloseDay(weekDays[dayIdx])) {
												<mat-icon class="close-day"
													matTooltip="Clôturer la journée : ajuster les heures réellement réalisées"
													(click)="onCloseDay(weekDays[dayIdx])">task_alt</mat-icon>
											}
										</span>
									</div>
									@if (weekDays[dayIdx].holiday) {
										<div class="holiday-name">{{ weekDays[dayIdx].holiday }}</div>
									}
									<div class="day-axis">
										@for (m of weekDays[dayIdx].markers; track $index) {
											<span class="axis-label" [class.close]="m.kind === 'close'" [style.left.%]="m.left_pct">{{ m.label }}</span>
										}
										<div class="axis-line">
											@for (b of weekDays[dayIdx].bands; track $index) {
												<span class="axis-band" [style.left.%]="b.left_pct" [style.width.%]="b.width_pct"></span>
											}
											@for (m of weekDays[dayIdx].markers; track $index) {
												<span class="axis-mark" [class.close]="m.kind === 'close'" [style.left.%]="m.left_pct"
													[matTooltip]="(m.kind === 'open' ? 'Ouverture ' : 'Fermeture ') + m.label"></span>
											}
										</div>
									</div>
								</th>
							}
							<th class="total-col">Semaine</th>
						</tr>
					</thead>
					<tbody>
						@for (row of posteRows; track row.id_activity) {
							<tr>
								<td class="name-col">
									<span class="poste-dot" [style.background]="row.color"></span>{{ row.name }}
								</td>
								@for (dayIdx of visibleDayIndices; track dayIdx) {
									<td class="poste-cell" [class.holiday]="weekDays[dayIdx].holiday" [class.today]="weekDays[dayIdx].today">
										@for (e of row.cells[dayIdx].entries; track e.slot) {
											<div class="slot-track poste-track" #ptrk>
												@for (b of weekDays[dayIdx].bands; track $index) {
													<span class="track-band" [style.left.%]="b.left_pct" [style.width.%]="b.width_pct"></span>
												}
												<!-- Vue Postes : la couleur dit QUI, le libellé aussi. Draggable comme partout. -->
												<span class="slot-bar" [class.absent]="e.slot.absent" [class.pending-absence]="e.slot.absence_pending"
													[style.left.%]="barOf(e.slot).left_pct"
													[style.width.%]="barOf(e.slot).width_pct"
													[style.background]="e.slot.absent ? null : employeeColor(e.slot.id_employee)"
													[style.color]="e.slot.absent ? null : (employeeColor(e.slot.id_employee) | hrTextOn)"
													[matTooltip]="e.name + ' — ' + placeLabel(e.slot) + ' · ' + slotLabel(e.slot)"
													(click)="$event.stopPropagation()"
													(pointerdown)="onBarPointerDown($event, e.slot, ptrk, 'move')">
													@if (!e.slot.absent) {
														<span class="handle left" (pointerdown)="onBarPointerDown($event, e.slot, ptrk, 'start')"></span>
													}
													<span class="bar-text">{{ e.name }}</span>
													@if (!e.slot.absent) {
														<span class="handle right" (pointerdown)="onBarPointerDown($event, e.slot, ptrk, 'end')"></span>
													}
												</span>
											</div>
										}
										@if (row.cells[dayIdx].entries.length > 0) {
											<div class="cell-sub">{{ row.cells[dayIdx].people }} pers. — {{ hours(row.cells[dayIdx].minutes) }}</div>
										}
									</td>
								}
								<td class="total-col">{{ hours(row.total_min) }}</td>
							</tr>
						}
						<!-- Ajout naturel : place une personne sur un poste (elle n'a pas de ligne propre ici). -->
						@if (isHrManager) {
						<tr class="add-emp-row">
							<td class="name-col">
								<mat-form-field class="add-emp-select" subscriptSizing="dynamic">
									<mat-label>＋ salarié</mat-label>
									<mat-select [formControl]="addEmpCtrl">
										@for (e of addableEmployees; track e.id) {
											<mat-option [value]="e.id">{{ e.name }}</mat-option>
										}
										@if (addableEmployees.length === 0) {
											<mat-option [disabled]="true">Aucun salarié actif à placer</mat-option>
										}
									</mat-select>
								</mat-form-field>
							</td>
							@for (dayIdx of visibleDayIndices; track dayIdx) {
								<td class="poste-cell empty"></td>
							}
							<td class="total-col"></td>
						</tr>
						}
					</tbody>
					<tfoot>
						<tr>
							<td class="name-col">Total</td>
							@for (dayIdx of visibleDayIndices; track dayIdx) {
								<td class="poste-cell foot">
									{{ dayTotals(dayIdx).people }} pers.<br>{{ hours(dayTotals(dayIdx).minutes) }}
								</td>
							}
							<td class="total-col"></td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	}

	@if (!loading && sections.length === 0) {
		<div class="hr-empty">
			{{ storeCtrl.value == null ? 'Aucun emploi du temps actif.' : 'Aucun emploi du temps actif pour ce magasin.' }}
			<a routerLink="/hr/schedules">Créer un emploi du temps</a>
		</div>
	}
</div>
